import { acquireMicTrack } from "./sharedMic";

// One camera stream for the whole exam. ImageProctoring opens it and keeps it for the exam;
// VideoInterview records from a clone of its video track plus its own microphone, instead of
// opening the camera a second time. Two independent streams on the same camera caused black
// frames, and "camera not found" once they asked for different resolutions (2026-09-22).

// Asked for once, by ImageProctoring. "ideal" never fails: the browser picks the closest
// mode the camera supports, in the camera's own aspect ratio.
export const PROCTORING_CAMERA_CONSTRAINTS = {
  video: {
    width: { ideal: 1280 },
    height: { ideal: 720 },
    facingMode: "user",
  },
  audio: false,
};

// Long side of the frame sent for analysis, and of the copy stored as the proctoring photo.
// The stored copy stays near the old 550x450 size so photo storage doesn't grow.
export const ANALYSIS_FRAME_LONG_SIDE = 960;
export const STORED_FRAME_LONG_SIDE = 640;

export const getLiveVideoTrack = (stream) => {
  if (!stream || typeof stream.getVideoTracks !== "function") return null;
  return stream.getVideoTracks().find((track) => track.readyState === "live") || null;
};

// Builds the stream VideoInterview records: a clone of the shared camera track (same camera,
// not a second opening of it) plus a copy of the shared microphone track. Stopping this stream's
// tracks later stops only the clone and the microphone; the proctoring camera keeps running.
export const buildRecordingStream = async (sharedStream, mediaConstraints) => {
  const sourceTrack = getLiveVideoTrack(sharedStream);
  if (!sourceTrack) throw new Error("Shared camera stream is not live");

  const videoTrack = sourceTrack.clone();
  try {
    // Keep the recording at its usual size where the browser can scale a clone on its own.
    const { width, height, frameRate } = (mediaConstraints && mediaConstraints.video) || {};
    const sizeConstraints = {};
    if (width) sizeConstraints.width = width;
    if (height) sizeConstraints.height = height;
    if (frameRate) sizeConstraints.frameRate = frameRate;
    if (Object.keys(sizeConstraints).length) {
      await videoTrack.applyConstraints(sizeConstraints).catch((err) => {
        console.warn("Recording track kept the camera's size (applyConstraints failed):", err && err.name);
      });
    }

    // A copy of the exam's one microphone capture (utils/sharedMic.js), not a second capture.
    const micTrack = await acquireMicTrack();
    return new MediaStream([videoTrack, micTrack]);
  } catch (err) {
    videoTrack.stop();
    throw err;
  }
};

// Draws the current video frame onto `canvas` at the video's own aspect ratio, with the long
// side capped at `longSide` (never upscaled). Returns false if the video has no frame yet.
export const drawVideoFrame = (video, canvas, longSide) => {
  if (!video || !canvas || video.readyState < 2 || !video.videoWidth || !video.videoHeight) return false;
  const scale = Math.min(1, longSide / Math.max(video.videoWidth, video.videoHeight));
  const width = Math.round(video.videoWidth * scale);
  const height = Math.round(video.videoHeight * scale);
  if (canvas.width !== width) canvas.width = width;
  if (canvas.height !== height) canvas.height = height;
  canvas.getContext("2d").drawImage(video, 0, 0, width, height);
  return true;
};

// Scales `source` (a canvas) down so its long side is at most `longSide`, onto `canvas`.
export const drawScaledCopy = (source, canvas, longSide) => {
  const scale = Math.min(1, longSide / Math.max(source.width, source.height));
  canvas.width = Math.round(source.width * scale);
  canvas.height = Math.round(source.height * scale);
  canvas.getContext("2d").drawImage(source, 0, 0, canvas.width, canvas.height);
  return canvas;
};
